import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiMail, FiPhone, FiCalendar } from "react-icons/fi";
import { FiLogOut, FiTrash2 } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, fetchWithAuth } from "../context/AuthContext";

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  // Prefer env-configured API URL, fall back to local dev
  const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profile, setProfile] = useState({
    fullName: '',
    // bio: '',
    email: '',
    phone: '',
  });


  // Populate form from auth user once loaded
  useEffect(() => {
    if (user) {
      setProfile({
        fullName: user.fullName || '',
        // bio: '',           // TODO: not in DB yet, leave blank or cleanup later
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: profile.fullName,
          phone: profile.phone,
        })
      });

      if (!res.ok) throw new Error('Failed to save');

      const updated = await res.json();
      setUser(prev => ({ ...prev, fullName: updated.fullName, phone: updated.phone }));
      // Update local profile state
      setProfile(prev => ({
        ...prev,
        fullName: updated.fullName || '',
        phone: updated.phone || '',
      }));

      setIsEditingProfile(false);
    } catch (err) {
      console.error('Save profile error:', err);
      // optionally show a toast error
    }
  };


  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  return (
    <Box minH="100vh" bg="#FFFFFF">
      <Navbar />

      <Box
        pt="clamp(80px, 9vh, 96px)"
        pb="clamp(32px, 4vh, 48px)"
        px="clamp(16px, 3vw, 40px)"
      >
        <Box maxW="1200px" mx="auto">
          <Flex align="center" gap="clamp(12px, 1.2vw, 16px)" mb="clamp(20px, 2vh, 28px)">
            <IconButton
              as={RouterLink}
              to="/"
              aria-label="Back to home"
              icon={<ArrowBackIcon boxSize="clamp(18px, 1.4vw, 22px)" />}
              variant="ghost"
              size="md"
              borderRadius="full"
              color="#242424"
              _hover={{ bg: "#F5F5F5", color: "#CF008A" }}
            />
            <Text
              fontSize="clamp(22px, 2vw, 28px)"
              fontWeight="semibold"
              color="#242424"
            >
              My Profile
            </Text>
          </Flex>

          <Flex
            gap="clamp(24px, 3vw, 40px)"
            align="flex-start"
            direction={{ base: "column", lg: "row" }}
          >
            {/* Left profile card */}
            <Box
              w={{ base: "100%", lg: "320px" }}
              bg="white"
              border="1px solid #ECECEC"
              borderRadius="20px"
              p="clamp(20px, 2vw, 24px)"
              boxShadow="sm"
            >
              <VStack align="stretch" spacing="clamp(16px, 1.5vw, 20px)">
                <VStack spacing="8px" align="center">
                  <Box
                    w="88px"
                    h="88px"
                    borderRadius="full"
                    bgGradient="linear(to-br, #FFD6F6, #CDEBFF)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Avatar
                      name={profile.fullName}
                      src={user?.avatarUrl || undefined}
                      size="xl"
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      bg="#CF008A"
                      color="white"
                    />
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="16px" fontWeight="semibold" color="#242424">
                      {profile.fullName || '—'}
                    </Text>
                    <Text fontSize="14px" color="#6D6D6D">
                      {user?.field || 'Member'}
                    </Text>
                  </Box>
                </VStack>

                <VStack align="stretch" spacing="10px" fontSize="14px" color="#4F4F4F">
                  {/* <HStack spacing="10px">
                    <Box as={FiMail} color="#6D6D6D" />
                    <Text>{profile.email}</Text>
                  </HStack>
                  <HStack spacing="10px">
                    <Box as={FiPhone} color="#6D6D6D" />
                    <Text>{profile.phone || '—'}</Text>
                  </HStack> */}
                  <HStack spacing="10px">
                    <Box as={FiCalendar} color="#6D6D6D" />
                    <Text>Joined on {user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}</Text>
                  </HStack>
                </VStack>
              </VStack>
            </Box>

            {/* Right content */}
            <VStack flex="1" align="stretch" spacing="clamp(24px, 2.5vw, 32px)">
              {/* My Profile section */}
              <Box>
                <Flex
                  align="center"
                  justify="space-between"
                  mb="clamp(12px, 1vh, 16px)"
                >
                  <Text fontSize="16px" fontWeight="bold">
                    My Profile
                  </Text>
                  {!isEditingProfile ? (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="pink"
                      borderColor="#CF008A"
                      color="#CF008A"
                      fontWeight="semibold"
                      borderRadius="10px"
                      _hover={{ bg: "#FFF0FA" }}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      bg="#CF008A"
                      color="white"
                      fontWeight="semibold"
                      borderRadius="10px"
                      _hover={{ bg: "#D933A1" }}
                      onClick={handleSaveProfile}
                    >
                      Save
                    </Button>
                  )}
                </Flex>
                <VStack
                  align="stretch"
                  spacing="clamp(16px, 1.5vw, 20px)"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Full name
                    </FormLabel>
                    <Input
                      value={profile.fullName}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, fullName: e.target.value }))
                      }
                      readOnly={!isEditingProfile}
                      h="44px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={{
                        borderColor: "#CF008A",
                        boxShadow: "0 0 0 1px #CF008A",
                      }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl>

                  {/* <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Bio
                    </FormLabel>
                    <Textarea
                      value={profile.bio}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, bio: e.target.value }))
                      }
                      readOnly={!isEditingProfile}
                      minH="100px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={{
                        borderColor: "#CF008A",
                        boxShadow: "0 0 0 1px #CF008A",
                      }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl> */}

                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Email address
                    </FormLabel>
                    <Input
                      type="email"
                      readOnly={true}
                      value={profile.email}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, email: e.target.value }))
                      }
                      h="44px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={{
                        borderColor: "#CF008A",
                        boxShadow: "0 0 0 1px #CF008A",
                      }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Phone number
                    </FormLabel>
                    <Input
                      value={profile.phone}
                      onChange={(e) =>
                        setProfile((p) => ({ ...p, phone: e.target.value }))
                      }
                      readOnly={!isEditingProfile}
                      h="44px"
                      borderRadius="12px"
                      border="1px solid #E0E0E0"
                      _hover={
                        isEditingProfile
                          ? { borderColor: "#D0D0D0" }
                          : undefined
                      }
                      _focus={{
                        borderColor: "#CF008A",
                        boxShadow: "0 0 0 1px #CF008A",
                      }}
                      _readOnly={{
                        bg: "#FAFAFA",
                        cursor: "default",
                        borderColor: "#ECECEC",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Delete account */}
              {/* <Box>
                <Text
                  fontSize="16px"
                  fontWeight="bold"
                  mb="clamp(12px, 1vh, 16px)"
                >
                  Delete account
                </Text>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap="clamp(12px, 1.5vw, 16px)"
                  align={{ base: "stretch", md: "center" }}
                  justify="space-between"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <Text fontSize="14px" color="#6D6D6D" maxW="360px">
                    Deleting your account will permanently remove all your data and cannot be undone.
                  </Text>
                  <HStack spacing="10px">
                    <Button
                      bg="#DE2424"
                      _hover={{ bg: "#F04747" }}
                      color="white"
                      borderRadius="12px"
                      h="44px"
                      px="clamp(16px, 1.8vw, 24px)"
                      leftIcon={<FiLogOut size={16} />}
                      onClick={handleLogout}
                    >
                      Log out
                    </Button>
                    <Button
                      leftIcon={<FiTrash2 size={16} />}
                      variant="outline"
                      borderColor="#FF5A5A"
                      color="#DE2424"
                      borderRadius="12px"
                      h="44px"
                      px="clamp(16px, 1.8vw, 24px)"
                      _hover={{ bg: "#FFF5F5" }}
                    >
                      Delete account
                    </Button>
                  </HStack>
                </Flex>
              </Box> */}

              {/* Logout */}
              <Box>
                <Text fontSize="16px" fontWeight="bold" mb="clamp(12px, 1vh, 16px)">
                  Session
                </Text>
                <Flex
                  align="center"
                  justify="space-between"
                  bg="white"
                  borderRadius="16px"
                  border="1px solid #F2F2F2"
                  p="clamp(16px, 2vw, 20px)"
                >
                  <Box>
                    <Text fontSize="14px" fontWeight="medium" color="#4F4F4F">
                      Log out of your account
                    </Text>
                    <Text fontSize="14px" color="#6D6D6D" mt="2px">
                      You can always log back in anytime.
                    </Text>
                  </Box>
                  <Button
                    leftIcon={<FiLogOut size={16} />}
                    variant="outline"
                    borderColor="#CF008A"
                    color="#CF008A"
                    borderRadius="12px"
                    h="44px"
                    px="clamp(16px, 1.8vw, 24px)"
                    _hover={{ bg: "#FDF0F8" }}
                    onClick={handleLogout}
                  >
                    Log out
                  </Button>
                </Flex>
              </Box>
            </VStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

